
import { supabase } from "@/integrations/supabase/client";
import { EmailResult, SendEmailParams } from "./types";
import { toast } from "@/hooks/use-toast";

/**
 * Sends an email using the Resend API via Supabase Edge Function
 */
export const sendEmail = async (params: SendEmailParams & { 
  attachments?: Array<{
    filename: string;
    content: string; // base64 content
    type: string;
  }>
}): Promise<EmailResult> => {
  try {
    console.log("Sending email to:", params.to, "Subject:", params.subject);
    console.log("Email params:", { 
      ...params, 
      html: params.html.substring(0, 100) + "...",
      attachments: params.attachments ? `${params.attachments.length} attachments` : 'No attachments'
    });
    
    const { data, error } = await supabase.functions.invoke("send-email", {
      body: {
        ...params,
        emailType: params.emailType || "general" // Add email type to track in logs
      }
    });

    console.log("Edge function response:", data, error);

    if (error) {
      console.error("Error invoking send-email function:", error);
      
      // Show a toast with more helpful information for common errors
      if (error.message.includes("Failed to send a request")) {
        toast({
          title: "Edge Function Error",
          description: "There may be an issue with the Edge Function or the RESEND_API_KEY. Please check the Edge Function logs.",
          variant: "destructive"
        });
        
        return {
          success: false,
          error: `Edge function connection error: Please verify your RESEND_API_KEY is set in Supabase Dashboard > Settings > Functions > send-email > Environment variables`
        };
      }
      
      return {
        success: false,
        error: `Edge function error: ${error.message}`
      };
    }

    // Check for API-level errors in the response
    if (data.error || (data.success === false)) {
      console.error("Email service error:", data.error);
      let errorMessage = '';
      
      // Handle different error formats (string vs object)
      if (typeof data.error === 'string') {
        errorMessage = data.error;
      } else if (data.error && typeof data.error === 'object') {
        errorMessage = data.error.message || JSON.stringify(data.error);
      } else {
        errorMessage = 'Unknown error occurred';
      }
      
      // Add more detailed error messages for common errors
      if (data.details === 'missing_api_key') {
        errorMessage = "Missing API key: The Resend API key is not set in the Edge Function";
        toast({
          title: "Configuration Error",
          description: "Resend API key is missing. Please set it in Supabase Dashboard > Settings > Functions > send-email.",
          variant: "destructive"
        });
      } else if (data.details === 'unauthorized') {
        errorMessage = "API key error: The Resend API key may be invalid or missing";
      } else if (errorMessage.includes('domain is not verified')) {
        errorMessage = "Domain verification required: Please verify your domain in Resend dashboard";
        toast({
          title: "Domain Not Verified",
          description: "Your domain needs to be verified in Resend. Visit https://resend.com/domains to verify your domain.",
          variant: "destructive"
        });
      } else if (errorMessage.includes('already been used')) {
        errorMessage = "This email address has already been used";
      } else if (errorMessage.includes('rate limit')) {
        errorMessage = "Rate limit exceeded: Too many emails sent in a short time";
      } else if (errorMessage.includes('invalid')) {
        errorMessage = "Invalid email address provided";
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }

    console.log("Email sent successfully, ID:", data.id);
    return {
      success: true,
      id: data.id
    };
  } catch (error: any) {
    console.error("Exception sending email:", error);
    
    toast({
      title: "Email Sending Failed",
      description: "An unexpected error occurred while sending the email",
      variant: "destructive"
    });
    
    return {
      success: false,
      error: error.message || "Unknown error sending email"
    };
  }
};

/**
 * Display email sending result with appropriate notifications
 */
export const displayEmailResult = (result: EmailResult, emailAddress: string) => {
  if (result.success) {
    console.log(`Email sent successfully to ${emailAddress}`);
    toast({
      title: "Email Sent",
      description: `Email successfully sent to ${emailAddress}`,
      variant: "success"
    });
  } else {
    console.error(`Failed to send email to ${emailAddress}:`, result.error);
    
    let errorMessage = 'Failed to send email.';
    if (result.error) {
      if (result.error.includes('invalid')) {
        errorMessage = `Invalid email address: ${emailAddress}`;
      } else if (result.error.includes('API key')) {
        errorMessage = `Email service configuration error: ${result.error}`;
      } else {
        errorMessage = `Error: ${result.error}`;
      }
    }
    
    toast({
      title: "Email Sending Failed",
      description: errorMessage,
      variant: "destructive"
    });
  }
};

/**
 * Fetch email logs for the current user
 */
export const fetchEmailLogs = async () => {
  try {
    const { data, error } = await supabase
      .from('email_logs')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  } catch (error: any) {
    console.error("Error fetching email logs:", error);
    throw error;
  }
};
